import React, { memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatHourlyChartMonthLabel } from './hourlyChartMonthWindow';

const navBtnClass =
  'inline-flex shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:bg-white disabled:hover:text-gray-600';

const navBtnCompactClass =
  'inline-flex shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white p-1 text-gray-600 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:bg-white disabled:hover:text-gray-600';

const sectionHeadingClass =
  'text-[10px] uppercase tracking-wide text-slate-400 text-center leading-none mb-1.5';

function MonthNavSection({
  year,
  monthIndex,
  onPrevious,
  onNext,
  compact = false,
}) {
  const label = formatHourlyChartMonthLabel(year, monthIndex);
  const atStart = monthIndex <= 0;
  const atEnd = monthIndex >= 11;
  const btnClass = compact ? navBtnCompactClass : navBtnClass;
  const iconClass = compact ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const labelClass = compact
    ? 'min-w-0 flex-1 text-center text-xs font-medium text-gray-800 tabular-nums px-0.5 leading-snug'
    : 'min-w-[9.5rem] text-center text-sm font-medium text-gray-700 tabular-nums';

  return (
    <div className="flex w-full items-center justify-center gap-1">
      <button
        type="button"
        className={btnClass}
        onClick={onPrevious}
        disabled={atStart}
        aria-label="Previous month"
        title="Previous month"
      >
        <ChevronLeft className={iconClass} aria-hidden />
        <span className="sr-only">Previous month</span>
      </button>
      <span className={labelClass} aria-live="polite">
        {label}
      </span>
      <button
        type="button"
        className={btnClass}
        onClick={onNext}
        disabled={atEnd}
        aria-label="Next month"
        title="Next month"
      >
        <ChevronRight className={iconClass} aria-hidden />
        <span className="sr-only">Next month</span>
      </button>
    </div>
  );
}

function MaxShortageNavSection({
  timestamp,
  navIndex,
  eventCount,
  onPrevious,
  onNext,
}) {
  const atStart = navIndex <= 0;
  const atEnd = navIndex >= eventCount - 1;

  return (
    <div className="flex w-full items-center justify-center gap-1">
      <button
        type="button"
        className={navBtnCompactClass}
        onClick={onPrevious}
        disabled={atStart}
        aria-label="Previous max shortage"
        title="Previous max shortage"
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        <span className="sr-only">Previous max shortage</span>
      </button>
      <span
        className="min-w-0 flex-1 text-center text-[11px] font-medium text-gray-800 tabular-nums px-0.5 leading-snug line-clamp-2"
        aria-live="polite"
        title={timestamp || undefined}
      >
        {timestamp}
      </span>
      <button
        type="button"
        className={navBtnCompactClass}
        onClick={onNext}
        disabled={atEnd}
        aria-label="Next max shortage"
        title="Next max shortage"
      >
        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        <span className="sr-only">Next max shortage</span>
      </button>
    </div>
  );
}

/**
 * Compact-viewport month picker for the hourly simulation chart (no API calls).
 * On mobile, when Max Shortage data exists, renders a split row with section headings.
 */
function HourlyChartMonthNavigator({
  year,
  monthIndex,
  onPrevious,
  onNext,
  className = '',
  showMaxShortageNav = false,
  maxShortageTimestamp = null,
  maxShortageNavIndex = 0,
  maxShortageEventCount = 0,
  onMaxShortagePrevious = null,
  onMaxShortageNext = null,
}) {
  const useSplitLayout =
    showMaxShortageNav &&
    maxShortageEventCount > 0 &&
    maxShortageTimestamp &&
    typeof onMaxShortagePrevious === 'function' &&
    typeof onMaxShortageNext === 'function';

  if (useSplitLayout) {
    return (
      <div
        className={`flex items-stretch gap-3 px-2 pt-2.5 pb-2 ${className}`.trim()}
      >
        <div
          className="flex min-w-0 flex-1 flex-col items-center px-0.5"
          role="group"
          aria-label="Chart period navigation"
        >
          <span className={sectionHeadingClass}>Chart Period</span>
          <MonthNavSection
            year={year}
            monthIndex={monthIndex}
            onPrevious={onPrevious}
            onNext={onNext}
            compact
          />
        </div>
        <div
          className="flex min-w-0 flex-1 flex-col items-center px-0.5"
          role="group"
          aria-label="Max shortage navigation"
        >
          <span className={sectionHeadingClass}>Max Shortage</span>
          <MaxShortageNavSection
            timestamp={maxShortageTimestamp}
            navIndex={maxShortageNavIndex}
            eventCount={maxShortageEventCount}
            onPrevious={onMaxShortagePrevious}
            onNext={onMaxShortageNext}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center gap-2 px-3 sm:px-4 lg:px-6 pt-2.5 pb-2 ${className}`.trim()}
      role="group"
      aria-label="Chart month navigation"
    >
      <MonthNavSection
        year={year}
        monthIndex={monthIndex}
        onPrevious={onPrevious}
        onNext={onNext}
      />
    </div>
  );
}

export default memo(HourlyChartMonthNavigator);
